import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_FELLOWSHIP_LOAD,
    ACTION_FELLOWSHIP_LOAD_BY_ID,
    ACTION_FELLOWSHIP_LOAD_BY_PERSONNEL_ID,
    ACTION_FELLOWSHIP_LOAD_FAILURE,
    ACTION_FELLOWSHIP_LOAD_SUCCESS,

    ACTION_FELLOWSHIP_SAVE,
    ACTION_FELLOWSHIP_SAVE_SUCCESS,
    ACTION_FELLOWSHIP_SAVE_FAILURE,

    ACTION_FELLOWSHIP_CREATE,
    ACTION_FELLOWSHIP_CREATE_SUCCESS,
    ACTION_FELLOWSHIP_CREATE_FAILURE,

    ACTION_FELLOWSHIP_VALIDATE,
    ACTION_FELLOWSHIP_VALIDATE_SUCCESS,
    ACTION_FELLOWSHIP_VALIDATE_FAILURE,

    ACTION_FELLOWSHIP_CLEAR,
    ACTION_FELLOWSHIP_FILTER,

    FellowshipAction,
} from 'src/actions/fellowship';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IFellowshipState {
    formData: Dtos.Fellowship | Dtos.Fellowship[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Fellowship[];
    formLength: number;
}

const initialState: IFellowshipState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IFellowshipState, _isLoading: boolean): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IFellowshipState,
    formData: Dtos.Fellowship | Dtos.Fellowship[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IFellowshipState, formFilter: IFormFilter): IFellowshipState {

    const fellowships: Dtos.Fellowship[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = fellowships ? fellowships.length : 0;

    const formFilteredData = fellowships ? fellowships.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.projectTitle.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IFellowshipState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IFellowshipState, responseStatus: Dtos.ResponseStatus): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IFellowshipState, propertiesToClear?: string[]): IFellowshipState {

    if (!propertiesToClear) {
        const newState: IFellowshipState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IFellowshipState, _isSaving: boolean): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IFellowshipState,
    formData: Dtos.Fellowship | Dtos.Fellowship[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IFellowshipState, responseStatus: Dtos.ResponseStatus): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IFellowshipState, _isCreating: boolean): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IFellowshipState,
    formData: Dtos.Fellowship,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IFellowshipState, responseStatus: Dtos.ResponseStatus): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IFellowshipState, _isValidating: boolean): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IFellowshipState,
    formData: Dtos.Fellowship,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IFellowshipState, responseStatus: Dtos.ResponseStatus): IFellowshipState {
    const newState: IFellowshipState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const FellowshipReducer: Reducer<IFellowshipState> = (state: IFellowshipState = initialState, action: FellowshipAction) => {
    switch (action.type) {
        case ACTION_FELLOWSHIP_LOAD:
        case ACTION_FELLOWSHIP_LOAD_BY_ID:
        case ACTION_FELLOWSHIP_LOAD_BY_PERSONNEL_ID:
            return loading(state, true);
        case ACTION_FELLOWSHIP_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_FELLOWSHIP_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_FELLOWSHIP_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_FELLOWSHIP_SAVE:
            return saving(state, true);
        case ACTION_FELLOWSHIP_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_FELLOWSHIP_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_FELLOWSHIP_CREATE:
            return creating(state, true);
        case ACTION_FELLOWSHIP_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_FELLOWSHIP_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_FELLOWSHIP_VALIDATE:
            return validating(state, true);
        case ACTION_FELLOWSHIP_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_FELLOWSHIP_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_FELLOWSHIP_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default FellowshipReducer;